export default function HowItWorks() {
  const steps = [
    {
      number: '01',
      title: 'We Build a Custom Preview',
      description:
        'We design a fully custom website preview built specifically around your Nashville business — your industry, your brand, your style. This is not a template; every preview is created from scratch just for you.',
    },
    {
      number: '02',
      title: 'We Customize Every Detail',
      description:
        'Every website we build is 100% custom to your exact specifications. Your branding, your content, your services, your goals — nothing is copied or reused from another project. We revise until every detail is exactly right for your business.',
    },
    {
      number: '03',
      title: 'We Launch Your Website',
      description:
        'Once you approve the final custom design, we publish your site with full hosting, SSL security, and ongoing support. Your unique website goes live and starts working for your business — in days, not months.',
    },
  ];

  return (
    <section id="how-it-works" className="py-20 lg:py-32 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 lg:mb-20">
          <div className="inline-block px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-6">
            <span className="text-sm text-gray-300 font-medium">Our Process</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-6">How We Build Your Nashville Website</h2>
          <p className="text-base sm:text-xl text-gray-400 max-w-2xl mx-auto">
            Getting your Nashville business online is simple with our AI-powered three-step process — no technical knowledge required
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-6 sm:gap-12">
          {steps.map((step, index) => (
            <div
              key={index}
              className="relative group cursor-pointer"
            >
              <div className="bg-black border border-white/10 rounded-2xl p-6 sm:p-10 hover:bg-white/5 transition-all duration-300 h-full">
                <div className="relative">
                  <div className="text-6xl sm:text-8xl font-thin text-gray-800 mb-6">{step.number}</div>
                  <div className="w-1 h-12 sm:h-16 bg-white/10 absolute top-0 left-12 sm:left-16"></div>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-4">{step.title}</h3>
                <p className="text-gray-400 leading-relaxed text-sm sm:text-base">{step.description}</p>
                {index === 1 && (
                  <div className="mt-5 flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/15 rounded-lg">
                    <div className="w-4 h-4 flex items-center justify-center shrink-0">
                      <i className="ri-shield-check-line text-white text-xs"></i>
                    </div>
                    <span className="text-xs text-gray-300 font-medium">100% custom — never a template</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Custom-built callout */}
        <div className="mt-12 sm:mt-16 border border-white/10 rounded-2xl p-6 sm:p-10 bg-white/[0.02]">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
            <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-xl shrink-0">
              <i className="ri-pencil-ruler-2-line text-white text-xl"></i>
            </div>
            <div>
              <h4 className="text-white font-bold text-lg mb-1">Every Website is Fully Custom-Built</h4>
              <p className="text-gray-400 text-sm sm:text-base leading-relaxed">
                We never reuse designs, templates, or layouts from previous projects. Every website we deliver is built from the ground up to match your brand, your voice, and your business goals — guaranteed to be 100% unique to you.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 sm:mt-10 text-center">
          <p className="text-gray-400 mb-6 text-sm sm:text-base">
            Nashville businesses are live in days — not months.{' '}
            <strong className="text-white">No contracts. No surprises. No templates.</strong>
          </p>
        </div>
      </div>
    </section>
  );
}